/**
 * Where a wall-length edit's translate-box goes by default, and where its
 * CUTTING edge may sit. ONE rule for the floor plan's editor
 * (`hooks/useStretchPlan`) and for `computeStretch` when it is handed no box,
 * so what the corpus measures is what the app does.
 *
 * The box is `[boundary, farBoundary] × [extent.min, extent.max]` in the
 * edit's `(dir, perp)` frame. Of its two edges along `dir`, the one strictly
 * between the edited wall's two ends is the CUT: the wall's end on one side
 * moves, the other stays. A typed edit and a drag on the wall's near (mover)
 * end cut with the near edge; a drag on its far (anchor) end cuts with the far
 * edge (`boundaryForGrabbedEnd`).
 *
 * The cut must not sit inside a junction — a tight group of wall ends and
 * floor corners that moves as one. Inside one, the group's own verdict and
 * the walls around it disagree about which side of the cut they are on: on
 * `E5DED26F` a drag on a kitchen wall's far end left two walls off their
 * floor, and on Lake Francois's bathroom the same drag was refused for a
 * slant it would never have made. Offset T-junctions (an end within 30 cm of
 * another wall's body) are no-go too, as before, and so is the thickness of a
 * wall square to the edit (`wallThicknessRanges`). The cut leaves any of these
 * by the nearer side that keeps it between the wall's ends.
 *
 * Pure. No React, no Firestore.
 */

import {
  PARALLEL_DOT,
  buildJunctionGraph,
  wallEndpointsXZ,
} from './wallDisplacement';
import { boundaryForGrabbedEnd } from './wallLengthDrag';

// The cut sits inside the edited wall, never exactly on an end.
export const BOUNDARY_INSET = 1e-3; // 1 mm
// The default box's far and side edges sit just outside the plan: visible,
// grabbable, and the same as the old open half-plane since nothing is there.
export const REGION_MARGIN = 0.3; // 30 cm
// How far past a no-go range the cut lands: every point of the range then
// sits on one side of it (`PARALLEL_TOL` is far below this).
export const CUT_SNAP_EPS = 1e-3; // 1 mm

// A wall end within this distance of another wall's body is an offset
// T-junction (or, near the other wall's end, an offset L-junction): the cut
// can't fall between the end and the body, or they'd classify apart.
const T_JUNCTION_TOL = 0.3; // 30 cm
// Ends this close already share a junction, which `junctionGroupRanges`
// covers.
const ENDPOINT_CLUSTER_TOL = 0.15; // 15 cm — wallDisplacement's ENDPOINT_TOL

const proj = (p, dir) => p.x * dir.x + p.z * dir.z;

/**
 * The plan's extent along `dir` and across it (`perp` = dir turned a quarter):
 * every structure wall end and floor corner.
 */
export function stretchRegionBounds(structure, dir) {
  if (!structure || !dir) return null;
  const perp = { x: -dir.z, z: dir.x };
  let minD = Infinity;
  let maxD = -Infinity;
  let minP = Infinity;
  let maxP = -Infinity;
  const consider = (x, z) => {
    const d = x * dir.x + z * dir.z;
    const p = x * perp.x + z * perp.z;
    if (d < minD) minD = d;
    if (d > maxD) maxD = d;
    if (p < minP) minP = p;
    if (p > maxP) maxP = p;
  };
  for (const w of structure.walls || []) {
    if (!w?.center || !w?.dimensions) continue;
    const { a, b } = wallEndpointsXZ(w);
    consider(a.x, a.z);
    consider(b.x, b.z);
  }
  for (const room of structure.rooms || []) {
    for (const c of room.floors?.[0]?.polygonCorners || []) {
      const x = c?.x !== undefined ? c.x : c?.[0];
      const z = c?.z !== undefined ? c.z : c?.[1];
      if (Number.isFinite(x) && Number.isFinite(z)) consider(x, z);
    }
  }
  if (!Number.isFinite(minD)) return null;
  return { minD, maxD, minP, maxP };
}

/**
 * Ranges along `dir` the cut can't fall in without splitting an offset T- or
 * L-junction: a wall end within T_JUNCTION_TOL of another wall's body, where
 * the two don't already share a junction. Diagonal walls aren't ranged —
 * crossing one re-angles it with a warning, which is the user's call.
 */
export function tJunctionRanges(structure, dir) {
  if (!structure || !dir) return [];
  const ranges = [];
  const wallEnds = (structure.walls || [])
    .filter((w) => w && w.center && w.dimensions && w.dimensions.x > 0)
    .map((wall) => wallEndpointsXZ(wall));

  for (let i = 0; i < wallEnds.length; i++) {
    for (const ep of [wallEnds[i].a, wallEnds[i].b]) {
      const epProj = proj(ep, dir);
      for (let j = 0; j < wallEnds.length; j++) {
        if (i === j) continue;
        const { a, b } = wallEnds[j];
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const lenSq = dx * dx + dz * dz;
        if (lenSq < 1e-9) continue;
        const tRaw = ((ep.x - a.x) * dx + (ep.z - a.z) * dz) / lenSq;
        const t = Math.max(0, Math.min(1, tRaw));
        const cx = a.x + t * dx;
        const cz = a.z + t * dz;
        const dist = Math.hypot(ep.x - cx, ep.z - cz);
        if (dist > T_JUNCTION_TOL || dist < 1e-6) continue;
        // Skip only an L whose two ends already share a junction.
        const isAtOtherEndpoint = tRaw <= 0.02 || tRaw >= 0.98;
        if (isAtOtherEndpoint && dist < ENDPOINT_CLUSTER_TOL) continue;
        const cProj = cx * dir.x + cz * dir.z;
        const lo = Math.min(epProj, cProj);
        const hi = Math.max(epProj, cProj);
        if (hi - lo > 1e-6) ranges.push([lo, hi]);
      }
    }
  }
  return ranges;
}

/**
 * The span along `dir` of every junction with more than one point — the
 * groups `computeStretch` classifies whole (`buildJunctionGraph`).
 */
export function junctionGroupRanges(structure, dir) {
  if (!structure || !dir) return [];
  const ranges = [];
  for (const j of buildJunctionGraph(structure).junctions) {
    if (j.members.length < 2) continue;
    let lo = Infinity;
    let hi = -Infinity;
    for (const m of j.members) {
      const d = proj(m, dir);
      if (d < lo) lo = d;
      if (d > hi) hi = d;
    }
    if (hi - lo > 1e-6) ranges.push([lo, hi]);
  }
  return ranges;
}

/**
 * The thickness of every wall square to `dir`, as a range along it. A cut
 * inside one puts the wall's centreline on one side and the floor corners on
 * its face on the other: the wall moves and the floor does not, or the other
 * way round, a whole change apart. Junctions hold the two together only while
 * the face is within `ENDPOINT_TOL` of the centreline, so a wall 30 cm thick
 * or more falls through (Curlew split-level and `F6D83996`, both 31 cm), and
 * a T's floor corners, which group with the partition's end and not with the
 * wall they meet, fall through at any thickness.
 */
export function wallThicknessRanges(structure, dir) {
  if (!structure || !dir) return [];
  const perp = { x: -dir.z, z: dir.x };
  const ranges = [];
  for (const w of structure.walls || []) {
    if (!w?.center || !w.dimensions || !(w.dimensions.z > 0)) continue;
    const { dir: u } = wallEndpointsXZ(w);
    if (Math.abs(u.x * perp.x + u.z * perp.z) < PARALLEL_DOT) continue;
    const c = proj(w.center, dir);
    const half = w.dimensions.z / 2;
    ranges.push([c - half, c + half]);
  }
  return ranges;
}

/**
 * Every no-go range for a cut: offset T-junctions, junction groups and wall
 * thicknesses. Pass every floor the edit can reach
 * (`reachableStructureForMeasurement`): an edit recruits another floor
 * through a shared wall, and a cut placed against the edited floor alone
 * landed inside a wall on the recruited one (`FB5B0707`, a T on the floor
 * above left 15 cm off its wall). A floor it can never reach is left out:
 * its walls only push the cut about.
 */
export function structuralCutRanges(structure, dir) {
  return [
    ...tJunctionRanges(structure, dir),
    ...junctionGroupRanges(structure, dir),
    ...wallThicknessRanges(structure, dir),
  ];
}

/**
 * Move a cut out of the no-go ranges. Ranges that touch are one range — the
 * old one-range-at-a-time snap could leave one and land in the next — and
 * the cut leaves it by the nearer side that stays within `[min, max]`.
 *
 * @returns {{ value: number, range: [number, number]|null, stuck: boolean }}
 *   `range` is the merged range the cut was in; `stuck` when neither side of
 *   it is within the limits, and then the cut is left where it was.
 */
export function snapCutOutOfRanges(
  value,
  ranges,
  { min = -Infinity, max = Infinity } = {}
) {
  if (!Number.isFinite(value)) return { value, range: null, stuck: false };
  const sorted = (ranges || [])
    .filter((r) => r && Number.isFinite(r[0]) && Number.isFinite(r[1]))
    .map(([a, b]) => [Math.min(a, b), Math.max(a, b)])
    .sort((p, q) => p[0] - q[0]);
  const merged = [];
  for (const r of sorted) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1] + CUT_SNAP_EPS)
      last[1] = Math.max(last[1], r[1]);
    else merged.push([...r]);
  }
  const hit = merged.find(
    ([lo, hi]) => value > lo - CUT_SNAP_EPS && value < hi + CUT_SNAP_EPS
  );
  if (!hit) return { value, range: null, stuck: false };
  const exits = [hit[0] - CUT_SNAP_EPS, hit[1] + CUT_SNAP_EPS]
    .filter((v) => v >= min && v <= max)
    .sort((p, q) => Math.abs(p - value) - Math.abs(q - value));
  if (!exits.length) return { value, range: hit, stuck: true };
  return { value: exits[0], range: hit, stuck: false };
}

/**
 * Which of the box's two edges along `dir` is the cut — the one strictly
 * between the edited wall's ends — or null when neither is (or both are).
 */
export function cutEdgeOf(region, axisInfo) {
  if (!region || !axisInfo) return null;
  const lo = Math.min(axisInfo.anchorProj, axisInfo.moverProj);
  const hi = Math.max(axisInfo.anchorProj, axisInfo.moverProj);
  const inside = (v) => Number.isFinite(v) && v > lo && v < hi;
  const near = inside(region.boundary);
  const far = inside(region.farBoundary);
  if (near === far) return null;
  return near ? 'near' : 'far';
}

/**
 * The range a box edge may be moved within: the near edge from just outside
 * the plan up to the far edge, the far edge from the near edge out to just
 * past the plan.
 */
export function edgeLimits(edge, region, bounds) {
  if (edge === 'near') {
    return {
      min: bounds ? bounds.minD - REGION_MARGIN : -Infinity,
      max: Number.isFinite(region?.farBoundary) ? region.farBoundary : Infinity,
    };
  }
  return {
    min: Number.isFinite(region?.boundary) ? region.boundary : -Infinity,
    max: bounds ? bounds.maxD + REGION_MARGIN : Infinity,
  };
}

/**
 * Re-settle a box's cut against `ranges` (the structural ranges plus, for a
 * shrink, the walls it would collapse — `computeBoundaryClampZones`).
 */
export function resnapCut(region, axisInfo, bounds, ranges) {
  const edge = cutEdgeOf(region, axisInfo);
  if (!edge) return { region, range: null };
  const key = edge === 'near' ? 'boundary' : 'farBoundary';
  const snapped = snapCutOutOfRanges(
    region[key],
    ranges,
    edgeLimits(edge, region, bounds)
  );
  if (snapped.value === region[key]) return { region, range: snapped.range };
  return { region: { ...region, [key]: snapped.value }, range: snapped.range };
}

/**
 * The box a length edit opens with. A typed edit cuts at the middle of the
 * edited wall; a drag cuts just inside the grabbed end (`grabbedPoint`). The
 * cut then leaves any junction group, offset T-junction or wall thickness,
 * preferring a spot clear of locked walls too (`lockRanges`), and stays
 * between the wall's ends. The far and side edges cover the plan with a
 * margin.
 *
 * @param {Object} structure - every floor the edit can reach
 *   (`reachableStructureForMeasurement`): its no-go ranges are where the cut
 *   may not fall (`structuralCutRanges`)
 * @param {{ dir, anchor, mover, anchorProj, moverProj }} axisInfo
 * @param {{ grabbedPoint?: {x,z}|null, lockRanges?: Array<[number,number]>,
 *   bounds?: Object }} [opts] - `bounds` sizes the far and side edges; pass
 *   the WHOLE structure's (`stretchRegionBounds`): the edit may recruit
 *   another floor, and a box sized by the edited floor alone cut that floor's
 *   walls with its side edge and refused the edit (Curlew, `7657745E`).
 * @returns {{ boundary: number, farBoundary: number,
 *   extent: { min: number, max: number } } | null}
 */
export function defaultStretchRegion(
  structure,
  axisInfo,
  { grabbedPoint = null, lockRanges = [], bounds: givenBounds = null } = {}
) {
  if (!axisInfo) return null;
  const { dir, anchorProj, moverProj } = axisInfo;
  const bounds = givenBounds || stretchRegionBounds(structure, dir);
  if (!bounds) return null;
  const extent = {
    min: bounds.minP - REGION_MARGIN,
    max: bounds.maxP + REGION_MARGIN,
  };
  let boundary;
  let farBoundary;
  let cutKey = 'boundary';
  if (grabbedPoint) {
    const g = boundaryForGrabbedEnd(
      axisInfo,
      grabbedPoint,
      bounds,
      REGION_MARGIN
    );
    boundary = g.boundary;
    farBoundary = g.farBoundary;
    cutKey = g.grabbedIsMover ? 'boundary' : 'farBoundary';
  } else {
    boundary = (anchorProj + moverProj) / 2;
    farBoundary = bounds.maxD + REGION_MARGIN;
  }
  const limits = {
    min: Math.min(anchorProj, moverProj) + BOUNDARY_INSET,
    max: Math.max(anchorProj, moverProj) - BOUNDARY_INSET,
  };
  const structural = structuralCutRanges(structure, dir);
  const seed = cutKey === 'boundary' ? boundary : farBoundary;
  // Clear of locked walls as well when that is possible; a lock only biases
  // the opening position, and the plan's lock check is what enforces it.
  let placed = snapCutOutOfRanges(seed, [...structural, ...lockRanges], limits);
  if (placed.stuck) placed = snapCutOutOfRanges(seed, structural, limits);
  const region = { boundary, farBoundary, extent };
  region[cutKey] = placed.value;
  return region;
}
